import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { UserInfo } from '../shared/user-info.model';
import { Subcontractor } from '../shared/subcontractor.model';
import { NavigationService } from '../../shared/services/navigation.service';
import { Carrier } from '../shared/carrier.model';
import { ActivatedRoute } from '@angular/router';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import {forkJoin, Observable, Subject, Subscription} from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '../../shared/services/loader.service';
import { ModalService } from '../../shared/services/modal.service';
import { BluechatService } from '../../bluechat/bluechat.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { FEATURES, FeatureService } from '../../shared/services/feature.service';
import {DataFiltersComponent} from '../../shared/data-filters/data-filters.component';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';

export type UserInfoWithNames = { user: UserInfo, firstname: string, lastname: string };

@Component({
  selector: 'app-user-table',
  templateUrl: './user-table.component.html',
  styleUrls: ['./user-table.component.scss']
})
export class UserTableComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  @Input()
  loadPage: (page: number, pageSize: number) => Observable<SearchObjectResult<UserInfo>>;

  @Input()
  activateBluechat: (userDetails: UserInfoWithNames) => Observable<UserInfo>;

  @Input()
  bluechatMultiUserActivationSubject: Subject<void> = new Subject<void>();

  @Input()
  showCarrier = false;

  @Input()
  showSubcontractor = false;

  @Input()
  showRegions = false;

  @Input()
  showPlants = false;

  @Input()
  showStatus = true;

  @Input()
  showDelete = false;

  @Input()
  showEdit = true;

  @Input()
  showBluechatColumns = false;

  @Input()
  showCheckboxes: boolean;

  @Input()
  filtersComponent: DataFiltersComponent;

  @Input()
  filters: any;

  @Input()
  showActionsColumn = false;

  @Input()
  actionsColumn: TemplateRef<any>;

  @Output()
  filtersChange = new EventEmitter<any>();

  @Output()
  edit = new EventEmitter<string>();

  @Output()
  delete = new EventEmitter<string>();

  @Output()
  filtersReset = new EventEmitter<void>();

  @ViewChild('bluechatActivationSingleUserModal')
  bluechatActivationSingleUserModal: TemplateRef<any>;

  @ViewChild('bluechatActivationMultiUsersModal')
  bluechatActivationMultiUsersModal: TemplateRef<any>;

  selectedUsers: Set<String> = new Set();

  pagedUsers: SearchObjectResult<UserInfo>;

  selfCarrierMode   = false;
  subcontractorMode = false;

  page = 1;
  pageSize = 10;
  lastPage = 1;
  pageSizeOptions = [10, 20, 30, 40, 50];
  bluechatActivationModalFirstname = null;
  bluechatActivationModalLastname = null;
  bluechatEnabled = false;

  private resetSubscription: Subscription;
  private applySubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private navigation: NavigationService,
    public link: NavigationLinkService,
    private modalService: ModalService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
    private bluechatService: BluechatService,
    private translate: TranslateService,
    private featureService: FeatureService,
  ) {
    this.route.data.subscribe(data => {
      if (data.selfCarrierMode) {
        this.selfCarrierMode = true;
      }

      if (data.subcontractorMode) {
        this.subcontractorMode = true;
      }
    });
  }

  ngOnInit() {
    if (this.bluechatMultiUserActivationSubject) {
      this.bluechatMultiUserActivationSubject.subscribe(() => {
        this.openEnableBluechatForSelectedUsers();
      });
    }

    this.featureService.getFeatureStatusObservable(FEATURES.BLUECHAT).subscribe(bluechatStatus => this.bluechatEnabled = bluechatStatus);
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onRefresh();
  }

  onRefresh() {
    if (this.loadPage) {
      this.getPage(this.page, this.pageSize);
    }
  }

  getPage(page, size) {
    this.loader.startLoad();
    this.loadPage(page - 1, size)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(res => {
        this.pagedUsers = res;
        this.lastPage = res.totalPages;
      });
  }

  editUser(id: string) {
    this.edit.emit(id);
  }

  deleteUser(id: string) {
    this.delete.emit(id);
  }

  openEnableBluchatForUserModal(user: UserInfo) {
    const firstnameAndLastname = this.bluechatService.deduceFirstnameAndLastNameFromEmail(user);
    this.bluechatActivationModalFirstname = firstnameAndLastname.firstname;
    this.bluechatActivationModalLastname = firstnameAndLastname.lastname;
    this.modalService.open({
      title: this.translate.instant('bluechat.modal.title'),
      content: this.bluechatActivationSingleUserModal,
      data: {
        user,
      }
    }).subscribe( () => {
      this.loader.startLoad();
      this.activateBluechat({ user, firstname: this.bluechatActivationModalFirstname, lastname: this.bluechatActivationModalLastname })
        .pipe(
          finalize(() => this.loader.stopLoad()),
        )
        .subscribe( () => {
          this.toastr.success(`Bluechat enabled for ${user.email}`);
          this.onRefresh();
        }, err => this.errorHandler.handleHttpError(err));
    });
  }

  openEnableBluechatForSelectedUsers() {
    const users = this.pagedUsers.results.filter(el => this.selectedUsers.has(el.id));

    if (users.length === 0) {
      return;
    }

    this.modalService.open({
      title: this.translate.instant('bluechat.modal.title'),
      content: this.bluechatActivationMultiUsersModal,
    }).subscribe( () => {
      this.loader.startLoad();
      forkJoin(users.map(u => this.activateBluechat({ user: u, ...this.bluechatService.deduceFirstnameAndLastNameFromEmail(u) })))
        .pipe(
          finalize(() => this.loader.stopLoad()),
        )
        .subscribe( newUsersInfo => {
          newUsersInfo.forEach(newUserInfo => {
            const userIndex = this.pagedUsers.results.findIndex(user => user.id === newUserInfo.id);
            if (userIndex !== -1) {
              this.pagedUsers.results[userIndex] = Object.assign(this.pagedUsers.results[userIndex], {bluechatId: newUserInfo.bluechatId});
            }
          });

          this.toastr.success(this.translate.instant('bluechat.activated-users'));
        }, err => this.errorHandler.handleHttpError(err));
    })
  }

  onUserCheckChange(user: UserInfo, checked: boolean) {
    if (checked) {
      user.selected = true;
      this.selectedUsers = this.selectedUsers.add(user.id);
    } else {
      user.selected = false;
      this.selectedUsers.delete(user.id);
    }
  }

  selectAll(checked: boolean) {
    this.pagedUsers.results
      .filter(user => !user.bluechatEnabled())
      .forEach(user => {
        this.onUserCheckChange(user, checked);
      });
  }

  ngOnDestroy(): void {
    if (this.bluechatMultiUserActivationSubject) {
      this.bluechatMultiUserActivationSubject.unsubscribe();
    }
    if (this.resetSubscription) {
      this.resetSubscription.unsubscribe();
    }
    if (this.applySubscription) {
      this.applySubscription.unsubscribe();
    }
  }

  /** Filters **/

  ngAfterViewInit(): void {
    if (this.filtersComponent) {
      this.resetSubscription = this.filtersComponent.getResetObservable().subscribe(() => this.reset());
      this.applySubscription = this.filtersComponent.getApplyObservable().subscribe(() => this.refresh());
    }
  }

  private reset() {
    this.filtersReset.emit();
    this.filtersChange.emit(this.filters);

    this.refresh();
  }

  private refresh() {
    this.getPage(1, this.pageSize);
  }
}
